import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { MultiSelect } from 'react-multi-select-component';
import CustomTooltip from '../../../../../Components/Tooltip/CustomTooltip';
import SimpleFilter from './SimpleFilter';
import { shortenandHoverString } from '../../../../../Components/helper';

function ProductPurchaseOrderHeader({
  columnDropdownOptions,
  selectedColumns,
  handleSelectedColumns,
  searchState,
  handleSearch,
  handleClearSearch,
  state,
  allTierList,
  ColorLibList,
  filterState,
  setFilterState,
  handleApplyFilter,
  expandSearchRef,
  handleIconClick,
  handleRevisedColumns,
  handleActualColumns,
  totalCount,
}) {
  const { t } = useTranslation('purchase-order');

  // Translation key constants
  const SHOW_REVISED_COLUMNS = 'PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.SHOW_REVISED_COLUMNS';
  const HIDE_REVISED_COLUMNS = 'PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.HIDE_REVISED_COLUMNS';
  const SHOW_ACTUAL_COLUMNS = 'PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.SHOW_ACTUAL_COLUMNS';
  const HIDE_ACTUAL_COLUMNS = 'PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.HIDE_ACTUAL_COLUMNS';

  const statusList = useMemo(
    () => [
      { _id: 'approved', name: t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.STATUS_VALUES.APPROVED') },
      { _id: 'rejected', name: t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.STATUS_VALUES.REJECTED') },
      { _id: 'draft', name: t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.STATUS_VALUES.DRAFT') },
      { _id: 'active', name: t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.STATUS_VALUES.ACTIVE') },
      { _id: 'revised', name: t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.STATUS_VALUES.REVISED') },
      { _id: 'cancelled', name: t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.STATUS_VALUES.CANCELLED') },
      { _id: 'delivered', name: t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.STATUS_VALUES.DELIVERED') },
    ],
    [t],
  );

  return (
    <div className="page-title-filter sticky">
      <div className="title-container max-w-260">
        <h4 className="title d-inline-flex bom-title-list w-xs-200px">
          {shortenandHoverString(t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.PAGE_TITLE'), 30, 'bottom')}
        </h4>
      </div>
      <div className="ml-auto search-filter">
        <div className="action-icons-container">
          {totalCount !== undefined && (
            <div className="total-count-display mr-8 d-flex align-items-center">
              <span className="text-muted small">{t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.TOTAL_COUNT', { count: totalCount || 0 })}</span>
            </div>
          )}
          <MultiSelect
            name="po-todo-columns"
            options={columnDropdownOptions}
            className="bordered-multiselect common-new-multiselect w-200px"
            value={selectedColumns}
            labelledBy={t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.SELECT_COLUMNS')}
            overrideStrings={{ selectSomeItems: t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.SELECT_COLUMNS') }}
            onChange={(selected) => handleSelectedColumns(selected)}
          />
          <div className="search-filter ml-8 action-new-row cp-search">
            <div className="search-filter">
              <div ref={expandSearchRef} className="common-expand-search">
                <button type="button" onClick={handleIconClick} className="btn btn-toggle" aria-label={t('COMMON_LABEL.SEARCH', { ns: 'common' })}>
                  <span className="icon-Magnifying-Glass-Solid" />
                </button>
                <input
                  type="text"
                  value={searchState.searchText}
                  onChange={handleSearch}
                  disabled={state.isInitialLoading || state?.isTableLoading}
                  placeholder={t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.SEARCH_PLACEHOLDER')}
                />
                {searchState.searchText && (
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className="btn btn-clear-search"
                    aria-label={t('COMMON_LABEL.CLEAR', { ns: 'common' })}
                    title={t('COMMON_LABEL.CLEAR', { ns: 'common' })}
                  >
                    <span className="icon-Close" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Column visibility toggle buttons */}
          <div className="ml-8 d-flex">
            <CustomTooltip tooltipText={state.showRevisedColumns ? t(HIDE_REVISED_COLUMNS) : t(SHOW_REVISED_COLUMNS)} placement="top">
              <button
                type="button"
                className="btn btn-titlebar"
                onClick={() => handleRevisedColumns({ target: { checked: !state.showRevisedColumns } })}
                aria-label={state.showRevisedColumns ? t(HIDE_REVISED_COLUMNS) : t(SHOW_REVISED_COLUMNS)}
              >
                <i className="icon-eye mr-1" />
                {shortenandHoverString(state.showRevisedColumns ? t(HIDE_REVISED_COLUMNS) : t(SHOW_REVISED_COLUMNS), 25, 'top')}
              </button>
            </CustomTooltip>

            <CustomTooltip tooltipText={state.showActualColumns ? t(HIDE_ACTUAL_COLUMNS) : t(SHOW_ACTUAL_COLUMNS)} placement="top">
              <button
                type="button"
                className="btn btn-titlebar"
                onClick={() => handleActualColumns({ target: { checked: !state.showActualColumns } })}
                aria-label={state.showActualColumns ? t(HIDE_ACTUAL_COLUMNS) : t(SHOW_ACTUAL_COLUMNS)}
              >
                <i className="icon-eye mr-1" />
                {shortenandHoverString(state.showActualColumns ? t(HIDE_ACTUAL_COLUMNS) : t(SHOW_ACTUAL_COLUMNS), 25, 'top')}
              </button>
            </CustomTooltip>
          </div>

          <div className="product-bom ml-8">
            <SimpleFilter
              applyFilter={handleApplyFilter}
              supplierList={allTierList || []}
              colourwayList={ColorLibList || []}
              statusList={statusList}
              filterState={filterState}
              setFilterState={setFilterState}
            />
          </div>
        </div>
      </div>
      {/* Removed floating delete actions */}
    </div>
  );
}

ProductPurchaseOrderHeader.propTypes = {
  columnDropdownOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    }),
  ).isRequired,
  selectedColumns: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    }),
  ).isRequired,
  handleSelectedColumns: PropTypes.func.isRequired,
  searchState: PropTypes.shape({
    searchText: PropTypes.string,
    searchLoaded: PropTypes.bool,
  }).isRequired,
  handleSearch: PropTypes.func.isRequired,
  handleClearSearch: PropTypes.func.isRequired,
  state: PropTypes.shape({
    showRevisedColumns: PropTypes.bool,
    showActualColumns: PropTypes.bool,
    isInitialLoading: PropTypes.bool,
    isTableLoading: PropTypes.bool,
  }).isRequired,
  allTierList: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
      name: PropTypes.string,
    }),
  ),
  ColorLibList: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
      name: PropTypes.string,
    }),
  ),
  filterState: PropTypes.shape({
    supplierIds: PropTypes.arrayOf(PropTypes.string),
    colourwayIds: PropTypes.arrayOf(PropTypes.string),
    statuses: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  setFilterState: PropTypes.func.isRequired,
  handleApplyFilter: PropTypes.func.isRequired,
  expandSearchRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element),
  }).isRequired,
  handleIconClick: PropTypes.func.isRequired,
  handleRevisedColumns: PropTypes.func.isRequired,
  handleActualColumns: PropTypes.func.isRequired,
  totalCount: PropTypes.number,
};

ProductPurchaseOrderHeader.defaultProps = {
  allTierList: [],
  ColorLibList: [],
  totalCount: 0,
};

export default ProductPurchaseOrderHeader;
